import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Camera, Check, X, ArrowLeft, Image as ImageIcon, Monitor, Trash2, ExternalLink, Video, VideoOff, Smartphone } from 'lucide-react';
import { io } from 'socket.io-client';

const EventPhotosModeration = () => {
  const { slug, eventId } = useParams();
  const [photos, setPhotos] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:11200';

  // WebRTC State
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const localVideoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const socketRef = React.useRef(null);
  const peerConnectionsRef = React.useRef({}); // map of socketId -> RTCPeerConnection

  // QR Modal State
  const [showQRModal, setShowQRModal] = useState(false);
  const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
  const broadcasterLink = `${frontendUrl}/${slug}/events/${eventId}/broadcaster`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(broadcasterLink)}`;

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/${slug}/api/events/${eventId}/photos/admin`, {
        withCredentials: true,
      });
      setPhotos(res.data);
    } catch (err) {
      console.error(err);
      setError('Error al cargar las fotos.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, slug, eventId]);

  const fetchEvent = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/${slug}/api/events/${eventId}`);
      setEvent(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [apiUrl, slug, eventId]);

  useEffect(() => {
    fetchEvent();
    fetchPhotos();
    // Poll every 10 seconds for new photos
    const interval = setInterval(fetchPhotos, 10000);
    return () => clearInterval(interval);
  }, [fetchEvent, fetchPhotos]);

  // Attach local stream to video element when it mounts
  useEffect(() => {
    if (isStreaming && localVideoRef.current && streamRef.current) {
      localVideoRef.current.srcObject = streamRef.current;
    }
  }, [isStreaming]);

  // Clean up WebRTC on unmount
  useEffect(() => {
    return () => {
      stopStreaming();
    };
  }, []);

  const stopStreaming = () => {
    setIsStreaming(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.emit('stream-stopped', eventId);
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
    peerConnectionsRef.current = {};
  };

  const startStreaming = async () => {
    try {
      setStreamError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      
      setIsStreaming(true);

      // Connect to Socket
      const socket = io(apiUrl);
      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit('join-event', eventId);
        socket.emit('stream-started', eventId);
      });

      // When a viewer joins, create a peer connection for them
      socket.on('viewer-joined', async (viewerSocketId) => {
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        });
        peerConnectionsRef.current[viewerSocketId] = pc;

        // Add local stream tracks to the connection
        stream.getTracks().forEach(track => pc.addTrack(track, stream));

        // Handle ICE candidates
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            socket.emit('ice-candidate', { eventId, target: viewerSocketId, candidate: event.candidate });
          }
        };

        // Create offer
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('offer', { eventId, target: viewerSocketId, offer });
      });

      // Handle Answer from Viewer
      socket.on('answer', async (data) => {
        const pc = peerConnectionsRef.current[data.sender];
        if (pc) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
        }
      });

      // Handle ICE candidates from Viewer
      socket.on('ice-candidate', async (data) => {
        const pc = peerConnectionsRef.current[data.sender];
        if (pc && data.candidate) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        }
      });

    } catch (err) {
      console.error('Error accessing camera:', err);
      setStreamError('No se pudo acceder a la cámara. Revisa los permisos.');
    }
  };

  const handleUpdateStatus = async (photoId, status) => {
    try {
      await axios.put(`${apiUrl}/${slug}/api/events/${eventId}/photos/admin/${photoId}/status`, { status }, {
        withCredentials: true,
      });
      // Update local state smoothly
      setPhotos(prev => prev.map(p => p._id === photoId ? { ...p, status } : p));
    } catch (err) {
      alert('Error al actualizar el estado de la foto');
    }
  };

  const pendingPhotos = photos.filter(p => p.status === 'pending');
  const approvedPhotos = photos.filter(p => p.status === 'approved');
  const rejectedPhotos = photos.filter(p => p.status === 'rejected');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white p-6 pb-20 selection:bg-purple-500/30">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="bg-white/5 border border-white/10 rounded-[32px] p-6 lg:p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 blur-[80px] rounded-full pointer-events-none" />
          
          <div>
            <Link to={`/${slug}/admin/events`} className="inline-flex items-center gap-2 text-white/40 hover:text-white transition-colors text-sm font-bold uppercase tracking-widest mb-4">
              <ArrowLeft size={16} /> Volver a Eventos
            </Link>
            <h1 className="text-3xl lg:text-4xl font-black mb-2 flex items-center gap-3">
              <Camera className="text-purple-400" />
              Transmisiones
            </h1>
            <p className="text-white/60 font-medium">
              Modera las fotos enviadas por los invitados {event && `para ${event.title}`}
            </p>
          </div>

          <div className="flex flex-wrap gap-4 relative z-10">
            <button
              onClick={() => setShowQRModal(true)}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-2xl flex items-center gap-2 font-bold transition-all text-sm"
            >
              <Smartphone size={18} />
              Vincular Celular
            </button>
            {isStreaming ? (
              <button
                onClick={stopStreaming}
                className="px-6 py-3 bg-red-500 hover:bg-red-600 rounded-2xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-red-500/25 text-sm"
              >
                <VideoOff size={16} /> Detener Cámara
              </button>
            ) : (
              <button
                onClick={startStreaming}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-2xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-blue-500/25 text-sm"
              >
                <Video size={16} /> Iniciar Cámara en Vivo
              </button>
            )}
            <Link
              to={`/${slug}/events/${eventId}/upload`}
              target="_blank"
              className="px-6 py-3 bg-white/5 border border-white/10 hover:bg-white/10 rounded-2xl flex items-center gap-2 font-bold transition-all text-sm"
            >
              <ExternalLink size={16} /> Ver Link Invitados
            </Link>
            <Link
              to={`/${slug}/events/${eventId}/projection`}
              target="_blank"
              className="px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-2xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-purple-500/25 text-sm"
            >
              <Monitor size={16} /> Abrir Proyección
            </Link>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-2xl text-sm font-medium">
            {error}
          </div>
        )}
        
        {streamError && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-2xl text-sm font-medium">
            {streamError}
          </div>
        )}

        {/* Live Camera Preview */}
        {isStreaming && (
          <div className="bg-black border border-white/10 rounded-[32px] overflow-hidden relative shadow-2xl animate-in fade-in zoom-in-95 duration-300">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1 bg-red-500/80 backdrop-blur-md rounded-full text-[10px] font-black uppercase tracking-widest text-white animate-pulse shadow-lg">
              <span className="w-2 h-2 rounded-full bg-white"></span>
              EN VIVO
            </div>
            <video 
              ref={localVideoRef} 
              autoPlay 
              playsInline 
              muted 
              className="w-full max-h-[60vh] object-cover"
            />
          </div>
        )}

        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 transition-all duration-500 ${isStreaming ? 'opacity-50 grayscale pointer-events-none' : ''}`}>
          
          {/* Pendientes (Left Column) */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center justify-between px-2">
              <h2 className="text-xl font-black flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]"></span>
                Pendientes
              </h2>
              <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-bold">{pendingPhotos.length}</span>
            </div>
            
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
              {pendingPhotos.length === 0 ? (
                <div className="bg-white/5 border border-white/5 rounded-[24px] p-8 text-center text-white/40">
                  <ImageIcon size={32} className="mx-auto mb-4 opacity-20" />
                  <p className="font-medium text-sm">No hay fotos pendientes de moderación.</p>
                </div>
              ) : (
                pendingPhotos.map(photo => (
                  <PhotoCard 
                    key={photo._id} 
                    photo={photo} 
                    onApprove={() => handleUpdateStatus(photo._id, 'approved')} 
                    onReject={() => handleUpdateStatus(photo._id, 'rejected')} 
                  />
                ))
              )}
            </div>
          </div>

          {/* Aprobadas (Middle Column) */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center justify-between px-2">
              <h2 className="text-xl font-black flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"></span>
                En Pantalla
              </h2>
              <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-bold">{approvedPhotos.length}</span>
            </div>
            
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
              {approvedPhotos.length === 0 ? (
                <div className="bg-white/5 border border-white/5 rounded-[24px] p-8 text-center text-white/40">
                  <Monitor size={32} className="mx-auto mb-4 opacity-20" />
                  <p className="font-medium text-sm">Aún no hay fotos aprobadas para proyectar.</p>
                </div>
              ) : (
                approvedPhotos.map(photo => (
                  <PhotoCard 
                    key={photo._id} 
                    photo={photo} 
                    isApproved 
                    onReject={() => handleUpdateStatus(photo._id, 'rejected')} 
                  />
                ))
              )}
            </div>
          </div>

          {/* Rechazadas (Right Column) */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center justify-between px-2">
              <h2 className="text-xl font-black text-white/50 flex items-center gap-2">
                <Trash2 size={16} /> Rechazadas / Ocultas
              </h2>
            </div>
            
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar opacity-60 hover:opacity-100 transition-opacity">
              {rejectedPhotos.map(photo => (
                <PhotoCard 
                  key={photo._id} 
                  photo={photo} 
                  isRejected 
                  onApprove={() => handleUpdateStatus(photo._id, 'approved')} 
                />
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* QR Modal */}
      {showQRModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#1a1a1a] border border-white/10 p-8 rounded-3xl max-w-sm w-full text-center relative shadow-2xl animate-in zoom-in-95 duration-200">
            <button 
              onClick={() => setShowQRModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors p-2 bg-white/5 rounded-full"
            >
              <X size={20} />
            </button>
            
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Smartphone size={32} className="text-white" />
            </div>
            
            <h3 className="text-2xl font-black text-white mb-2 tracking-tight">Operador Móvil</h3>
            <p className="text-white/60 text-sm mb-8 font-medium">
              Escanea este código con la cámara de tu celular para transmitir en vivo desde allí.
            </p>
            
            <div className="bg-white p-4 rounded-2xl shadow-xl inline-block mb-6">
              <img src={qrCodeUrl} alt="Broadcaster QR Code" className="w-48 h-48 rounded-lg" />
            </div>
            
            <p className="text-xs text-white/40 uppercase tracking-widest font-bold">
              La conexión es directa y pública
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const PhotoCard = ({ photo, onApprove, onReject, isApproved, isRejected }) => {
  return (
    <div className={`bg-white/5 border ${isApproved ? 'border-green-500/30' : isRejected ? 'border-red-500/20' : 'border-white/10'} rounded-[24px] overflow-hidden group hover:border-white/20 transition-all`}>
      <div className="relative aspect-square bg-black/50">
        <img src={photo.url} alt="Guest uploaded" className="w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 pb-2 pt-12">
          <p className="text-xs font-medium text-white/80 line-clamp-1">
            Enviado por: {photo.uploadedBy || 'Anónimo'}
          </p>
          <p className="text-[10px] text-white/40">
            {new Date(photo.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
      
      <div className="p-2 grid grid-cols-2 gap-2 bg-[#111111]">
        {!isApproved && (
          <button 
            onClick={onApprove}
            className={`flex items-center justify-center gap-2 p-3 rounded-xl font-bold text-xs transition-colors ${isRejected ? 'col-span-2 bg-white/5 hover:bg-white/10 text-white' : 'bg-green-500/10 hover:bg-green-500/20 text-green-400'}`}
          >
            <Check size={14} /> Aprobar
          </button>
        )}
        
        {!isRejected && (
          <button 
            onClick={onReject}
            className={`flex items-center justify-center gap-2 p-3 rounded-xl font-bold text-xs transition-colors ${isApproved ? 'col-span-2 bg-red-500/10 hover:bg-red-500/20 text-red-400' : 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white'}`}
          >
            <X size={14} /> Ocultar
          </button>
        )}
      </div>
    </div>
  );
};

export default EventPhotosModeration;
